'use server'

import { apiRequest, ApiResponse } from '../request'
import { getStoredToken } from '@/lib/api/session-refresh'
import { deliverPdf, PdfDelivery } from '@/lib/download-blob'

export interface Attachment {
    fileName: string
    filePath: string
    userName: string
    description: string
    details?: string | null
    createdAt?: string
}

export interface Note {
    userName: string
    note: string
    createdAt?: string
}

export interface Client {
    id: number
    name: string
    email?: string
    whatsAppNumber?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    organizationId: number
    createdAt: string
    updatedAt: string
    chatsCount?: number
    // Carteira de clientes (global por organização). Null = pool sem carteira.
    walletId?: number | null
    wallet?: {
        id: number
        name: string
    } | null
    // Fidelidade: saldo de pontos (1 ponto por R$1 gasto)
    pointsBalance?: number
    metadata?: Array<{ key: string; value: string }>
    attachments?: Attachment[]
    notes?: Note[]
    // PEP (Prontuario Eletronico) fields
    socialName?: string
    cpf?: string
    rg?: string
    rgIssuer?: string
    birthDate?: string
    gender?: string
    maritalStatus?: string
    nationality?: string
    birthPlace?: string
    occupation?: string
    motherName?: string
    fatherName?: string
    emergencyContactName?: string
    emergencyContactPhone?: string
    emergencyContactRelation?: string
    cns?: string
    bloodType?: string
    organDonor?: boolean
    photoUrl?: string
    partnerId?: number | null
    partner?: {
        id: number
        name: string
        tradingName?: string
    } | null
    professionalId?: number | null
    professional?: {
        id: number
        name: string
        formation?: string
    } | null
}

export interface CreateClientData {
    name: string
    cpf?: string
    email?: string
    whatsAppNumber?: string
    birthDate?: string
    gender?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    organizationId?: number
    metadata?: Array<{ key: string; value: string }>
    password?: string
    roleId?: number
    partnerId?: number | null
    professionalId?: number | null
}

export interface UpdateClientData {
    name?: string
    cpf?: string | null
    email?: string
    whatsAppNumber?: string
    birthDate?: string
    gender?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    metadata?: Array<{ key: string; value: string }>
    partnerId?: number | null
    professionalId?: number | null
}

/**
 * Não existe mais um `getClients()` sem paginação: a chamada devolvia a
 * organização inteira (29.838 clientes em produção) e travava o backend a cada
 * abertura de tela. Para escolher um cliente use o `ClientSelector`; para listar,
 * `getClientsByOrganization` com `page`/`limit`.
 */
export async function getClientsByOrganization(
    organizationId: number,
    params?: {
        page?: number
        limit?: number
        search?: string
    },
): Promise<ApiResponse<Client[]>> {
    const queryParams: Record<string, any> = {}
    if (params?.page) queryParams.page = params.page
    if (params?.limit) queryParams.limit = params.limit
    if (params?.search) queryParams.search = params.search
    return apiRequest<Client[]>(`clients/organization/${organizationId}`, {
        params: queryParams,
    })
}

export async function getClient(id: number): Promise<ApiResponse<Client>> {
    return apiRequest<Client>(`clients/${id}`, {})
}

export async function createClient(
    data: CreateClientData
): Promise<ApiResponse<Client>> {
    return apiRequest<Client>('clients', { data }, 'post')
}

export async function updateClient(
    id: number,
    data: UpdateClientData
): Promise<ApiResponse<Client>> {
    return apiRequest<Client>(`clients/${id}`, { data }, 'patch')
}

export async function deleteClient(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`clients/${id}`, {}, 'delete')
}

export async function uploadClientAttachment(
    id: number,
    file: File,
    userName: string,
    description: string,
    details?: string
): Promise<ApiResponse<Client>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('userName', userName)
    formData.append('description', description)
    if (details) {
        formData.append('details', details)
    }

    const { getClient } = await import('../client')
    const client = getClient(true)

    // A chave 'token' nunca existiu neste projeto — estas chamadas iam sem
    // credencial. O access token vive em memória.
    const token = getStoredToken()

    const response = await client.post(`clients/${id}/attachments`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
        },
    })

    return {
        success: true,
        result: response.data,
    }
}

export async function deleteClientAttachment(
    id: number,
    attachmentIndex: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `clients/${id}/attachments/${attachmentIndex}`,
        {},
        'delete'
    )
}

export async function addClientNote(
    id: number,
    note: string,
    userName: string
): Promise<ApiResponse<Client>> {
    return apiRequest<Client>(`clients/${id}/notes`, { data: { note, userName } }, 'post')
}

export async function deleteClientNote(
    id: number,
    noteIndex: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `clients/${id}/notes/${noteIndex}`,
        {},
        'delete'
    )
}

/**
 * Ficha cadastral do cliente em PDF.
 *
 * `delivery` decide entre abrir o diálogo de impressão e salvar o arquivo — quem
 * clica em "Imprimir" quer o papel, não um PDF na pasta de downloads.
 *
 * Não usa `apiRequest`: a resposta é um binário, e o wrapper trata tudo como JSON.
 */
export async function printClient(
    clientId: number,
    delivery: PdfDelivery = 'download'
): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const response = await fetch(`${baseUrl}/clients/${clientId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar a ficha em PDF')

    const blob = await response.blob()

    deliverPdf(blob, `ficha-${clientId}.pdf`, delivery)
}
